import React, { lazy, Suspense } from "react";
import AdminRoute from "components/AdminRoute";
import ErrorBoundary from "components/ErrorBoundary";
import FloatingChat from "components/FloatingChat";
import ProtectedRoute from "components/ProtectedRoute";
import ScrollToTop from "components/ScrollToTop";
import NotFound from "pages/NotFound";
import GenderRequiredModal from "./components/GenderRequiredModal";
import EmailVerificationModal from "./components/EmailVerificationModal";
import LegalReacceptanceModal from "./components/LegalReacceptanceModal";
import { useAuth } from "contexts/authProvider";
import {
  BrowserRouter,
  Route,
  Routes as RouterRoutes,
  Navigate,
} from "react-router-dom";
// Páginas carregadas sob demanda (code-splitting) — reduz o bundle inicial.
const ProfileDetails = lazy(() => import("./pages/profile-details"));
const UserDashboard = lazy(() => import("./pages/user-dashboard"));
const UserLogin = lazy(() => import("./pages/user-login"));
// TEMPORARIAMENTE DESABILITADO - import GiftMarketplace from './pages/gift-marketplace';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "contexts/authProvider";
import { FirebaseProvider } from "contexts/firebaseProvider";
import { NotificationProvider } from "contexts/notificationProvider";
import { PresenceProvider } from "contexts/presenceProvider";
import { ProfileQueueProvider } from "contexts/profileQueueProvider";
import { BlockProvider } from "contexts/blockProvider";
import { StripeProvider } from "contexts/stripeProvider";
import { ThemeProvider } from "contexts/themeProvider";
import { useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
const AccountDeletion = lazy(() => import("./pages/account-deletion"));
const PrivacyData = lazy(() => import("./pages/privacy-data"));
const BlockedUsers = lazy(() => import("./pages/blocked-users"));
const AdminDashboard = lazy(() => import("./pages/admin/dashboard"));
const AdminAnalytics = lazy(() => import("./pages/admin/analytics"));
const AdminLogin = lazy(() => import("./pages/admin/login"));
const AdminReports = lazy(() => import("./pages/admin/reports"));
const AdminReportsPage = lazy(() => import("./pages/admin/reports-page"));
const AdminCompliance = lazy(() => import("./pages/admin/compliance"));
const AdminReportDetail = lazy(
  () => import("./pages/admin/reports/report-detail"),
);
const AdminSupport = lazy(() => import("./pages/admin/support"));
const AdminTicketDetail = lazy(
  () => import("./pages/admin/support/ticket-detail"),
);
const AdminUsers = lazy(() => import("./pages/admin/users"));
const AdminPhotoModeration = lazy(
  () => import("./pages/admin/photo-moderation"),
);
const AdminTimelineModeration = lazy(
  () => import("./pages/admin/timeline-moderation"),
);
const AdminFirebaseCosts = lazy(() => import("./pages/admin/firebase-costs"));
const AdminWebhookEvents = lazy(() => import("./pages/admin/webhook-events"));
const AdminPayments = lazy(() => import("./pages/admin/payments"));
const AdvancedSearch = lazy(() => import("./pages/advanced-search"));
const ChatConversation = lazy(() => import("./pages/chat-conversation"));
const MatchesPage = lazy(() => import("./pages/matches"));
// TEMPORARIAMENTE DESABILITADO - const RatingSystem = lazy(() => import("./pages/rating-system"));
const TimelineFeed = lazy(() => import("./pages/timeline-feed"));
const SubscriptionPlans = lazy(() => import("./pages/subscription-plans"));
const SubscriptionCancel = lazy(
  () => import("./pages/subscription-plans/cancel"),
);
const SubscriptionSuccess = lazy(
  () => import("./pages/subscription-plans/success"),
);
const Support = lazy(() => import("./pages/support"));
const SupportTicketDetail = lazy(
  () => import("./pages/support/ticket-detail"),
);
const UserRegistration = lazy(() => import("./pages/user-registration"));
const ForgotPassword = lazy(() => import("./pages/forgot-password"));
const ResetPassword = lazy(() => import("./pages/reset-password"));
const VerifyEmail = lazy(() => import("./pages/verify-email"));
import logger from "utils/logger";
const queryClient = new QueryClient();

// Redireciona preservando a query string (ex.: ?chatId=...) ao normalizar
// rotas legadas para o prefixo /dash.
const RedirectPreservingQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

// Componente interno para acessar useLocation
const AppContent = () => {
  const location = useLocation();
  const { showGenderModal, showEmailVerificationModal, showLegalReacceptanceModal } =
    useAuth();

  // Páginas onde o FloatingChat não deve aparecer
  const publicPaths = [
    "/",
    "/user-login",
    "/user-registration",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/admin/login",
  ];
  const isChatPage =
    location.pathname === "/dash/chat-conversation" ||
    location.pathname === "/chat-conversation";
  const isTimelinePage =
    location.pathname === "/dash/timeline" || location.pathname === "/timeline";
  const isPaymentPage =
    location.pathname.startsWith("/dash/subscription") ||
    location.pathname.startsWith("/subscription");

  const shouldShowFloatingChat =
    !publicPaths.includes(location.pathname) && !isChatPage && !isTimelinePage && !isPaymentPage;

  logger.log(
    "AppContent - Pathname:",
    location.pathname,
    "Show FloatingChat:",
    shouldShowFloatingChat,
  );

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <ScrollToTop />
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-primary" />
          </div>
        }
      >
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<UserLogin />} />
        <Route path="/user-login" element={<UserLogin />} />
        <Route path="/user-registration" element={<UserRegistration />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Legacy Custom Redirects */}
        <Route
          path="/profile-details"
          element={<Navigate to="/dash/profile-details" replace />}
        />
        <Route
          path="/user-dashboard"
          element={<Navigate to="/dash/user-dashboard" replace />}
        />
        {/* TEMPORARIAMENTE DESABILITADO - sistema de avaliações. As rotas
            continuam existindo para não quebrar links antigos (notificações
            já enviadas, favoritos), mas redirecionam para o painel. */}
        <Route
          path="/rating-system"
          element={<Navigate to="/dash/user-dashboard" replace />}
        />
        <Route
          path="/subscription-plans"
          element={<Navigate to="/dash/subscription-plans" replace />}
        />
        <Route
          path="/subscription/success"
          element={<Navigate to="/dash/subscription/success" replace />}
        />
        <Route
          path="/subscription/cancel"
          element={<Navigate to="/dash/subscription/cancel" replace />}
        />
        <Route
          path="/chat-conversation"
          element={<RedirectPreservingQuery to="/dash/chat-conversation" />}
        />
        <Route
          path="/matches"
          element={<Navigate to="/dash/matches" replace />}
        />
        <Route
          path="/advanced-search"
          element={<Navigate to="/dash/advanced-search" replace />}
        />
        <Route
          path="/support"
          element={<Navigate to="/dash/support" replace />}
        />
        <Route
          path="/support/ticket/:ticketId"
          element={<Navigate to="/dash/support/ticket/:ticketId" replace />}
        />
        <Route
          path="/account-deletion"
          element={<Navigate to="/dash/account-deletion" replace />}
        />
        <Route
          path="/timeline"
          element={<Navigate to="/dash/feed" replace />}
        />
        <Route path="/feed" element={<Navigate to="/dash/feed" replace />} />

        <Route
          path="/dash/profile-details"
          element={
            <ProtectedRoute>
              <ProfileDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/user-dashboard"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />
        {/* TEMPORARIAMENTE DESABILITADO - <Route path="/gift-marketplace" element={<GiftMarketplace />} /> */}
        {/* TEMPORARIAMENTE DESABILITADO - sistema de avaliações
        <Route
          path="/dash/rating-system"
          element={
            <ProtectedRoute>
              <RatingSystem />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/dash/rating-system"
          element={<Navigate to="/dash/user-dashboard" replace />}
        />
        <Route
          path="/dash/subscription-plans"
          element={
            <ProtectedRoute>
              <SubscriptionPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/subscription/success"
          element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/subscription/cancel"
          element={
            <ProtectedRoute>
              <SubscriptionCancel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/chat-conversation"
          element={
            <ProtectedRoute>
              <ChatConversation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/matches"
          element={
            <ProtectedRoute>
              <MatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/advanced-search"
          element={
            <ProtectedRoute>
              <AdvancedSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/feed"
          element={
            <ProtectedRoute>
              <TimelineFeed />
            </ProtectedRoute>
          }
        />
        {/* Compatibilidade: notificações antigas de like/comentário apontam
            para /dash/timeline, que não existe. Redireciona para o feed. */}
        <Route
          path="/dash/timeline"
          element={<Navigate to="/dash/feed" replace />}
        />
        <Route
          path="/dash/support"
          element={
            <ProtectedRoute>
              <Support />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/support/ticket/:ticketId"
          element={
            <ProtectedRoute>
              <SupportTicketDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <AdminRoute>
              <AdminSupport />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/support/ticket/:ticketId"
          element={
            <AdminRoute>
              <AdminTicketDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/compliance"
          element={
            <AdminRoute>
              <AdminCompliance />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <AdminReports />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports/:id"
          element={
            <AdminRoute>
              <AdminReportDetail />
            </AdminRoute>
          }
        />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminRoute>
              <AdminAnalytics />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports-page"
          element={
            <AdminRoute>
              <AdminReportsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/photo-moderation"
          element={
            <AdminRoute>
              <AdminPhotoModeration />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/timeline-moderation"
          element={
            <AdminRoute>
              <AdminTimelineModeration />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/firebase-costs"
          element={
            <AdminRoute>
              <AdminFirebaseCosts />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/webhook-events"
          element={
            <AdminRoute>
              <AdminWebhookEvents />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <AdminRoute>
              <AdminPayments />
            </AdminRoute>
          }
        />
        <Route
          path="/dash/account-deletion"
          element={
            <ProtectedRoute>
              <AccountDeletion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/meus-dados"
          element={
            <ProtectedRoute>
              <PrivacyData />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dash/blocked-users"
          element={
            <ProtectedRoute>
              <BlockedUsers />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </Suspense>
      {shouldShowFloatingChat && <FloatingChat />}
      {/* Os gates não aparecem na própria página de verificação (onde o
          usuário está justamente confirmando o e-mail). Ordem de prioridade:
          e-mail, depois re-aceite dos documentos legais, depois gênero — sem
          conta confirmada não há a quem opor os Termos, e sem aceite dos
          Termos não se pede mais nenhum dado. */}
      {location.pathname !== "/verify-email" &&
        (showEmailVerificationModal ? (
          <EmailVerificationModal />
        ) : showLegalReacceptanceModal ? (
          <LegalReacceptanceModal />
        ) : (
          showGenderModal && <GenderRequiredModal />
        ))}
    </ErrorBoundary>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient} contextSharing={true}>
        <ThemeProvider>
          <FirebaseProvider>
            <AuthProvider>
              <NotificationProvider>
                <StripeProvider>
                  <PresenceProvider>
                    <ProfileQueueProvider>
                      <BlockProvider>
                      <ErrorBoundary>
                        <AppContent />
                        <Toaster />
                      </ErrorBoundary>
                      </BlockProvider>
                    </ProfileQueueProvider>
                  </PresenceProvider>
                </StripeProvider>
              </NotificationProvider>
            </AuthProvider>
          </FirebaseProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default Routes;
